<template>
  <v-container fluid fill-height ma-0 pa-1 >
    <v-layout fill-height pa-0>

      <loading-circle
        :show="loader"
      />

      <v-flex xs12 px-2>
        <v-layout column fill-height justify-start ma-0 pa-0 >
          <v-flex style="position: relative" >
            <v-layout fill-height>
              <scroll-table
                :data="getTable"
                :options="getOptions"
                :sortable="true"
                :sorted="sort"
                :headUppercase="false"
                :rowCursor="'pointer'"
                @select="(row) => onSelect(row)"
                @sort="(item) => sortTable(item)"
              />
            </v-layout>
          </v-flex>
        </v-layout>
      </v-flex>

    </v-layout>
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue'
import loadingCircle from '@/nms/components/partials/loader.vue'
import scrollTable from '@/nms/components/partials/scrolltable_new.vue'

// Типы
interface DeviceItem {
  $id?: string
  id?: string
  name?: string
  cls?: string
  template?: string
  ip_address?: string
  serial_number?: string
  mount?: string
  children?: DeviceItem[]
  links?: DeviceItem[]
  tags?: string[]
  _cmdb_?: boolean
  getLinks?: (options?: any) => Promise<DeviceItem[]>
  [key: string]: any
}

interface TableRow {
  device?: DeviceItem
  name?: string
  $id?: string
  template?: string
  ip_address?: string
  serial_number?: string
  mount?: string
  place?: string
  cabinet?: string
  [key: string]: any
}

interface TableColumn {
  title: string
  value: string
  type: string
  width: string
  iconName?: string
}

interface TableData {
  columns: TableColumn[]
  rows: TableRow[]
}

interface OptionsColumns {
  [key: string]: { width: string }
}

interface OptionsData {
  columns: OptionsColumns
}

interface EquipmentData {
  $id?: string
  id?: string
  name?: string
  cls?: string
  template?: string
  _cmdb_?: boolean
  links?: DeviceItem[]
  children?: DeviceItem[]
  getLinks?: (options?: any) => Promise<DeviceItem[]>
  [key: string]: any
}

export default Vue.extend({
  name: 'EQUIPMENT_TABLE',

  components: {
    loadingCircle,
    scrollTable,
  },

  props: {
    data: {
      type: Object as () => EquipmentData,
      default: () => ({})
    },
    devices: {
      type: Array as () => DeviceItem[],
      default: () => []
    },
    update: {
      type: Boolean,
      default: () => false
    }
  },

  data(): {
    idOnMount: string | null
    loader: boolean
    meta: Record<string, any>
    options: OptionsData
    filter: any | null
    search: string
    sort: string
    table: TableData
  } {
    return {
      idOnMount: null,
      loader: false,
      meta: {},
      options: {
        columns: {
          text: { width: '40%' },
          timestamp: { width: '40%' },
          actions: { width: '40%' },
          endpoint: { width: '40%' },
          check: { width: '20%' },
        },
      },
      filter: null,
      search: '',
      sort: '',
      table: {
        columns: [
          {
            title: 'device',
            value: 'name',
            type: 'text',
            width: '20%'
          },
          {
            title: 'template',
            value: 'template',
            type: 'text',
            width: '20%'
          },
          {
            title: 'state',
            value: '$id',
            type: 'icon',
            iconName: 'circle',
            width: '10%'
          },
          {
            title: 'ip address',
            value: 'ip_address',
            type: 'text',
            width: '10%'
          },
          {
            title: 'serial number',
            value: 'serial_number',
            type: 'text',
            width: '10%'
          },
        ],
        rows: []
      },
    }
  },

  computed: {
    getData(): EquipmentData {
      return this.data
    },

    getTable(): TableData {
      const t = this.$merge({}, this.table) as TableData
      
      if (this.search.length) {
        const searchLower = this.search.toLowerCase()
        t.rows = t.rows.filter(row => {
          return (row.name && row.name.toLowerCase().indexOf(searchLower) > -1) ||
                 (row.cabinet && row.cabinet.toLowerCase().indexOf(searchLower) > -1) ||
                 (row.place && row.place.toLowerCase().indexOf(searchLower) > -1)
        })
      }
      
      if (this.sort) {
        const isDescending = this.sort.charAt(0) === '-'
        const fieldName = isDescending ? this.sort.slice(1) : this.sort
        const direction = isDescending ? -1 : 1
        
        t.rows = [...t.rows].sort(this.$compare(fieldName, direction))
      }
      
      return t
    },

    getOptions(): OptionsData {
      return this.options
    },
  },

  watch: {
    data: {
      handler(newVal: EquipmentData): void {
        console.log('WATCH', this.$options.name, 'data', newVal)
        if (newVal.$id && newVal.$id !== this.idOnMount) {
          this.setup('WATCH data')
        }
      },
      deep: true
    },
    update: {
      handler(): void {
        this.setup('WATCH update')
      },
      deep: true
    },
    devices: {
      handler(newVal: DeviceItem[]): void {
        if (newVal) {
          this.setup('WATCH devices')
        }
      },
      deep: true
    },
  },

  mounted(): void {
    this.idOnMount = this.data.$id || null
    this.setup('mounted')
  },

  methods: {
    setup(caller: string): void {
      console.log(this.$options.name, 'SETUP caller', caller)
      this.reset()
      this.statusUnsubscribe('equipment-table-equipment')
      this.prepareTable()
    },

    onSelect(item: TableRow): void {
      this.$emit('select', item.$id)
    },

    async prepareTable(): Promise<void> {
      console.log(this.$options.name, 'prepareTable')
      
      try {
        let locationTree: DeviceItem[] = []
        let obj$: any

        if (this.getData._cmdb_) {
          if (this.getData.links?.length) {
            locationTree = this.getData.links
          } else if (this.getData.getLinks) {
            locationTree = await this.getData.getLinks()
          }
        } else {
          const query = {
            id: [this.getData.$id],
            data: {
              links: { flatten: false }
            }
          }
          obj$ = await this.$bridge.cmdbQuery(query)
          console.log(this.$options.name, 'prepareTable obj$', obj$)
          locationTree = obj$[0]?.links || []
        }

        if (this.getData.cls === 'service' && this.getData.template) {
          console.log(this.$options.name, 'prepareTable', 'service', this.getData)
          const query = {
            id: ['class:device', 'class:module'],
            notNull: true,
            filter: { tags: this.getData.template },
            data: { namePath: true, children: false }
          }
          
          const objects = await this.$bridge.cmdbQuery(query)
          if (objects?.length) {
            locationTree = objects
          }

          console.log(this.$options.name, 'prepareTable', 'locationTree', JSON.parse(JSON.stringify(locationTree)))
        }

        const arr: TableRow[] = []
        const subscribeArr: DeviceItem[] = []

        const heapData = (arr$: DeviceItem[], data: EquipmentData, parent?: string): void => {
          const parent$ = parent || this.getData.name || obj$?.[0]?.name || ''
          
          arr$.forEach(child => {
            const ownParent$ = (child.cls === 'location' || child.cls === 'cabinet') 
              ? `${parent$} / ${child.name}` 
              : parent$
            
            const row$: TableRow = {}
            
            if (child.cls === 'device') {
              row$.device = child
              row$.name = child.name
              row$.$id = child.$id
              row$.template = child.template
              row$.ip_address = child.ip_address || ''
              row$.serial_number = child.serial_number || ''
              row$.mount = this.$t(child.mount) || this.$t('front')
              row$.place = ownParent$
              row$.cabinet = data.cls === 'cabinet' ? data.name : this.$t('out of cabinet')
              arr.push(row$)
              subscribeArr.push(child)
            }

            if (child.cls === 'module' && data.cls === 'location') {
              row$.device = child
              row$.name = child.name
              row$.$id = child.$id
              row$.template = child.template
              row$.ip_address = child.ip_address || ''
              row$.serial_number = child.serial_number || ''
              row$.place = ownParent$
              row$.cabinet = this.$t('out of device')
              arr.push(row$)
              subscribeArr.push(child)
            }

            if (child.cls === 'module' && data.cls === 'service') {
              row$.device = child
              row$.name = child.name
              row$.$id = child.$id
              row$.template = child.template
              row$.ip_address = child.ip_address || ''
              row$.serial_number = child.serial_number || ''
              row$.place = ownParent$
              row$.cabinet = ''
              arr.push(row$)
              subscribeArr.push(child)
            }

            if (child.children?.length) {
              heapData(child.children, child as EquipmentData, ownParent$)
            }
          })
        }

        heapData(locationTree, this.getData)
        console.log(this.$options.name, 'PREPARE_TABLE', arr)
        this.$set(this.table, 'rows', arr)
        this.statusSubscribe(subscribeArr, 'equipment-table-equipment')
      } catch (err) {
        console.error('CATCH', this.$options.name, 'prepareTable', err)
      }
    },

    sortTable(item: TableColumn): void {
      if (this.sort === item.value) {
        this.sort = '-' + item.value
      } else {
        this.sort = item.value
      }
    },

    async statusSubscribe(arr: DeviceItem[], group: string): Promise<void> {
      this.$store.dispatch('measuresSubscribeInit', {
        handler: this.subscribeHandler,
        name: 'addStatus',
        group: group,
        listener: 'status'
      })
      
      if (arr.length) {
        arr.forEach(item => {
          if (item.$id) {
            this.$store.dispatch('addStatusKey', item)
          }
        })
        await this.$bridge.subscribe(arr.map(obj => obj.$id))
      }
    },

    subscribeHandler(e: { $id?: string; [key: string]: any }): void {
      if (e.$id) {
        this.$store.dispatch('addStatus', e)
      }
    },

    statusUnsubscribe(group: string): void {
      this.$store.dispatch('measuresSubscribeClose', {
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })
    },

    reset(): void {
      this.table.rows = []
    },
  },
})
</script>

<style>
</style>
